import { useState } from "react";
import { Filter, FilterMode } from "@/shared/lib/filter/filter";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { Check, ListFilter, RefreshCw, Search } from "lucide-react";
import t from "@/lang/lang";
import { useDebounce } from "@/shared/lib/debounce/debounce";
import { useCustomersCustomerListQuery } from "@/services/backend/customers/api/customer-api";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { cn } from "@/shared/lib/utils";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { oneLine } from "../../addresses/types/address";
import { CustomerAddressLine } from "./customer-address-line";

export function CustomerFilter({
  filter,
  onFilterChange,
  hidden = false,
}: {
  filter: Filter<string> | undefined;
  onFilterChange: (filter: Filter<string>) => void;
  hidden?: boolean;
}) {
  // This allows us to accept undefined filters,
  // which is common in list requests. This makes
  // it nicer to call the component.
  const filterVal = filter ?? { active: false, values: [] };

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { customers, searching, error } = useSearchCustomer(search);

  if (hidden) {
    return null;
  }

  const selectCustomer = (customerId: string) => () => {
    if (filterVal.values?.includes(customerId)) {
      return;
    }

    onFilterChange({
      ...filterVal,
      active: true,
      values: [...(filterVal.values ?? []), customerId],
    });

    setOpen(false);
    setSearch("");
  };

  const unselectCustomer = (customerId: string) => () => {
    const values = filterVal.values?.filter((v) => v !== customerId) ?? [];

    onFilterChange({
      ...filterVal,
      active: values.length > 0,
      values,
    });

    if (values.length === 0) {
      setOpen(false);
      setSearch("");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="justify-between" size="sm">
          <ListFilter className="mr-2 h-4 w-4" />
          <span>{t("Kunden")}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        {filterVal.values && filterVal.values.length > 0 && (
          <>
            <div className="px-3 pt-1.5 text-sm font-medium">
              {t("Aktuell Ausgewählt:")}
            </div>
            <div className="text-foreground border-b p-1">
              {filterVal.values.map((customerId) => (
                <button
                  type="button"
                  key={customerId}
                  onClick={unselectCustomer(customerId)}
                  className="hover:bg-accent flex w-full cursor-default items-center gap-4 rounded-sm px-2 py-1.5 text-sm outline-none select-none"
                >
                  <Check className="h-4 w-4" />
                  <div className="flex flex-col items-start">
                    <CustomerName customerId={customerId} />
                    <CustomerAddressLine
                      customerId={customerId}
                      className="text-muted-foreground text-xs"
                    />
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
        <div className="bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md">
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="placeholder:text-muted-foreground flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Suche...")}
            />
          </div>
        </div>
        {searching && (
          <div className="text-muted-foreground flex items-center p-3 text-sm">
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> {t("Suche ...")}
          </div>
        )}
        {!searching && error && (
          <div className="text-muted-foreground p-3 text-sm">{t("Fehler")}</div>
        )}
        {!searching &&
          !error &&
          search.length > 3 &&
          customers.length === 0 && (
            <div className="text-muted-foreground p-3 text-sm">
              {t("Keine Kunden gefunden")}
            </div>
          )}
        {customers.length > 0 && !error && (
          <>
            <div className="px-3 pt-1.5 text-sm font-medium">
              {t("Suchergebnisse:")}
            </div>
            <ScrollArea className="[&>[data-radix-scroll-area-viewport]]:max-h-[200px]">
              <div className="text-foreground p-1">
                {customers.map((customer) => (
                  <button
                    type="button"
                    key={customer.id}
                    onClick={selectCustomer(customer.id)}
                    className="hover:bg-accent flex w-full cursor-default items-center gap-4 rounded-sm px-2 py-1.5 text-sm outline-none select-none"
                  >
                    <Check
                      className={cn(
                        "h-4 w-4",
                        filterVal.values?.includes(customer.id)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <div className="flex flex-col items-start">
                      <span>{customer.name}</span>
                      <span className="text-muted-foreground text-xs">
                        {oneLine(customer.address)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

function useSearchCustomer(search: string) {
  const debouncedSearch = useDebounce(search, 250);

  const { data, isLoading, isFetching, error } = useCustomersCustomerListQuery(
    {
      name: {
        active: true,
        mode: FilterMode.Like,
        values: [debouncedSearch],
      },
      limit: 10,
    },
    { skip: !debouncedSearch || debouncedSearch.length < 3 },
  );

  const customers = data?.items ?? [];

  const searching = isLoading || isFetching;

  return { customers, searching, error };
}
